import { Link } from "react-router-dom";
import { Linkedin, Instagram, Mail, ArrowUpRight, MapPin } from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";

export const Footer = () => {
  const footerLinks = {
    product: [
      { name: "Features", href: "/#features" },
      { name: "Pricing", href: "/#pricing" },
      { name: "Services", href: "/services" },
    ],
    company: [
      { name: "About Us", href: "/about" },
      { name: "Contact", href: "/contact" },
      { name: "Blogs", href: "/blog" },
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
    ],
  };

  const socialLinks = [
    { icon: XIcon, href: "https://x.com/TheTechBus01", label: "X" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/thetechbusconnects", label: "LinkedIn" },
    { icon: Instagram, href: "https://www.instagram.com/the_tech_bus", label: "Instagram" },
    { icon: Mail, href: "mailto:support@thetechbus.com", label: "Email" },
  ];

  return (
    <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8 xl:px-12 relative">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent" />
      
      <div className="container mx-auto relative z-10">
        {/* Top Section */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5 mb-6">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="inline-block mb-3">
              <img
                src="/logo.png"
                alt="The Tech Bus"
                className="h-[4.75rem] w-auto max-w-[160px] object-contain object-left sm:h-12 sm:max-w-[180px] md:h-16 md:max-w-[240px] lg:h-[136px] lg:max-w-[420px]"
              />
            </Link>
            <p className="text-muted-foreground text-sm mb-3 max-w-xs leading-relaxed">
              Transforming ideas into exceptional digital experiences. 
              Building the future of technology, one innovation at a time.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-1.5 text-sm">
              <a href="mailto:support@thetechbus.com" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <Mail className="w-4 h-4" />
                support@thetechbus.com
              </a>
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                Gurugram, Haryana, India
              </div>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-heading font-semibold mb-2 text-sm">Product</h4>
            <ul className="space-y-1.5">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading font-semibold mb-2 text-sm">Company</h4>
            <ul className="space-y-1.5">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold mb-2 text-sm">Legal</h4>
            <ul className="space-y-1.5">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-border flex flex-col md:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} The Tech Bus. All rights reserved.
            </p>
          </div>
          
          {/* Social Links */}
          <div className="flex gap-2">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                aria-label={social.label}
                className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center hover:bg-primary/10 hover:text-primary transition-all"
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
